/**
 * What a sport's start list identifies its competitors by (user, 2026-11-03;
 * filled from the old OVR's screens, else the sport's real practice — the
 * reviewed sheet is docs/06-start-list-profiles.md).
 *
 * ONE table, keyed by sport and discipline CODES, never names (like
 * `match-format.ts`). The athlete page, the start list and the line-up read
 * it; a sport that changes its mind is one line here.
 *
 *   number    the athlete's number and the word shown for it; null = the
 *             sport uses none (rackets, fights, judged routines…)
 *   unique    SPORT = one holder per number in the whole sport (athletics);
 *             NOC = every team may wear a #10 (football); MEET = typed fresh
 *             per meet (weightlifting's lot)
 *   typedOn   ATHLETE = once on the athlete page, followed by every entry,
 *             roster and line-up of the sport; START_LIST = per meet only
 *   position  how a slot is placed in a meet
 */

export const ATHLETE_NUMBER_KINDS = ["BIB", "SHIRT", "CAP", "BACK", "SAIL", "HELMET", "LOT"] as const;
export type AthleteNumberKind = (typeof ATHLETE_NUMBER_KINDS)[number];

export const ATHLETE_NUMBER_SCOPES = ["SPORT", "NOC", "MEET"] as const;
export type AthleteNumberScope = (typeof ATHLETE_NUMBER_SCOPES)[number];

export const START_POSITION_KINDS = ["LANE", "ORDER", "SIDE", "CORNER", "GATE", "TARGET", "NONE"] as const;
export type StartPositionKind = (typeof START_POSITION_KINDS)[number];

export interface StartListProfile {
	number: AthleteNumberKind | null;
	unique: AthleteNumberScope | null;
	typedOn: "ATHLETE" | "START_LIST" | null;
	position: StartPositionKind;
}

const none = (position: StartPositionKind): StartListProfile => ({ number: null, unique: null, typedOn: null, position });
const athlete = (number: AthleteNumberKind, unique: "SPORT" | "NOC", position: StartPositionKind): StartListProfile => ({ number, unique, typedOn: "ATHLETE", position });
const bib = (position: StartPositionKind) => athlete("BIB", "SPORT", position);
const shirt = (number: "SHIRT" | "CAP" = "SHIRT") => athlete(number, "NOC", "SIDE");

const BY_SPORT: Readonly<Record<string, StartListProfile>> = {
	AQ: bib("LANE"),
	ARC: athlete("BACK", "SPORT", "TARGET"),
	AT: bib("LANE"),
	BD: none("SIDE"),
	BKB: shirt(),
	BIL: none("SIDE"),
	BOX: none("CORNER"),
	CRI: shirt(),
	CYC: bib("ORDER"),
	DSP: athlete("BACK", "SPORT", "ORDER"),
	EQU: bib("ORDER"),
	ESP: none("SIDE"),
	FEN: none("CORNER"),
	FB: shirt(),
	GLF: bib("ORDER"),
	GYM: bib("ORDER"),
	HOC: shirt(),
	IHO: shirt(),
	SKT: none("ORDER"),
	JUD: none("CORNER"),
	KAB: shirt(),
	KTE: none("CORNER"),
	LBW: athlete("BIB", "SPORT", "SIDE"),
	MUA: none("CORNER"),
	NET: none("SIDE"),
	PAD: none("SIDE"),
	PET: none("SIDE"),
	RUG: shirt(),
	SAL: athlete("SAIL", "SPORT", "NONE"),
	STK: shirt(),
	SHO: bib("TARGET"),
	SIL: none("CORNER"),
	SQU: none("SIDE"),
	TTE: none("SIDE"),
	TKD: none("CORNER"),
	TEN: none("SIDE"),
	BOW: bib("LANE"),
	TQB: none("SIDE"),
	TRI: bib("ORDER"),
	VBL: shirt(),
	WSK: bib("ORDER"),
	WLF: { number: "LOT", unique: "MEET", typedOn: "START_LIST", position: "ORDER" },
	WSH: none("CORNER"),
};

/** `<sport>/<discipline>` overrides — only where the discipline differs from its sport. */
const BY_DISCIPLINE: Readonly<Record<string, StartListProfile>> = {
	"AQ/DIV": bib("ORDER"),
	"AQ/ARS": none("ORDER"),
	"AQ/WPO": shirt("CAP"),
	"AQ/OWS": bib("NONE"),
	"AT/FLD": bib("ORDER"),
	"AT/RD": bib("NONE"),
	"DSP/BRK": none("CORNER"),
	"JUD/KATA": none("ORDER"),
	"KTE/KAT": bib("CORNER"),
	"MUA/WAI": none("ORDER"),
	"SKT/STR": athlete("HELMET", "SPORT", "ORDER"),
	"SHO/SHG": bib("ORDER"),
	"TKD/PMS": none("ORDER"),
	"WSH/TAO": bib("ORDER"),
};

/** A sport nobody has described yet: a bib, in start order. */
const FALLBACK: StartListProfile = bib("ORDER");

/** The start list profile of a sport (and discipline, when it has its own). Codes compare case-blind. */
export function startListProfileFor(sportCode: string | null | undefined, disciplineCode?: string | null): StartListProfile {
	const sport = (sportCode ?? "").trim().toUpperCase();
	const disc = (disciplineCode ?? "").trim().toUpperCase();
	return BY_DISCIPLINE[`${sport}/${disc}`] ?? BY_SPORT[sport] ?? FALLBACK;
}

/** The number an athlete is given on the athlete page in one sport: its word and how unique it is. */
export interface AthleteNumberRule {
	number: AthleteNumberKind;
	unique: "SPORT" | "NOC";
}

/**
 * The athlete page's number in a sport, from the disciplines the athlete is
 * entered in (a sport row holds ONE number per athlete). A discipline whose
 * number is sport-wide wins over a team one (a swimmer who also plays water
 * polo keeps a sport-wide bib); no entry yet = the sport's own profile.
 * Null = nothing of theirs in this sport takes a number from the athlete page.
 */
export function athleteNumberFor(sportCode: string, disciplineCodes: readonly string[]): AthleteNumberRule | null {
	const profiles = (disciplineCodes.length > 0 ? disciplineCodes : [null]).map((d) => startListProfileFor(sportCode, d)).filter((p) => p.typedOn === "ATHLETE" && p.number !== null);
	const pick = profiles.find((p) => p.unique === "SPORT") ?? profiles.find((p) => p.unique === "NOC");
	return pick?.number && (pick.unique === "SPORT" || pick.unique === "NOC") ? { number: pick.number, unique: pick.unique } : null;
}
